import React, { useState, useEffect, useMemo } from "react";
import { Plus, ChevronRight, Loader2, Search, Info } from "lucide-react";
import { MenuItem } from "../types";

import { Helmet } from "react-helmet-async";

interface MenuViewProps {
  onAddToCart: (item: any) => void;
  onCheckout: (item: any) => void;
  onAddonsLoaded?: (addons: any[]) => void;
  onOpenDetail?: (item: any) => void;
  isDark: boolean;
}

const SectionHeading: React.FC<{ title: string; subtitle?: string }> = ({
  title,
  subtitle,
}) => (
  <div className="text-center mb-10 px-4 space-y-3 animate-fade-in">
    <h2 className="text-3xl sm:text-5xl font-serif font-bold tracking-tight text-slate-900">
      {title}
    </h2>
    {subtitle && (
      <p className="text-slate-500 text-sm sm:text-lg max-w-2xl mx-auto font-medium">
        {subtitle}
      </p>
    )}
    <div className="h-1.5 w-16 bg-[#ee1d23] rounded-full mx-auto mt-4"></div>
  </div>
);

const MenuView: React.FC<MenuViewProps> = ({
  onAddToCart,
  onAddonsLoaded,
  onCheckout,
  onOpenDetail,
}) => {
  const [menuData, setMenuData] = useState<{ category: string; items: any[] }[]>([]);
  const [activeCategory, setActiveCategory] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchMenu = async () => {
      try {
        setIsLoading(true);

        const sheetUrl =
          "https://docs.google.com/spreadsheets/d/e/2PACX-1vTKumjwjsptdSA80QBwn-qGC2A-DIYnT8WijlWkHijcJYRlQz2DLwqGiFYD-OLkez9bPyStx_JMoVdK/pub?gid=683740645&single=true&output=csv";

        const response = await fetch(sheetUrl);
        const csvText = await response.text();

        const lines = csvText.split("\n");
        const headers = lines[0].split(",").map((h) => h.trim());
        const rows = lines.slice(1).map((line) => {
          const values = line.split(",");
          return headers.reduce(
            (obj, header, i) => {
              obj[header] = values[i] ? values[i].trim() : "";
              return obj;
            },
            {} as Record<string, string>
          );
        });

        const processedCategories: any[] = [];
        const allItemsCategory = { category: "SEMUA", items: [] as any[] };
        const promoCategory = { category: "PROMO", items: [] as any[] };

        rows.forEach((item: any) => {
          if (!item["ID"]) return;

          const menuItem = {
            ...item,
            name: item["Nama Menu"] || "Menu Hisana",
            priceNum: Number(item["Harga"]) || 0,
            description: item["Deskripsi"] || "",
            image: item["URL Gambar"]
              ? item["URL Gambar"].replace(
                  "/upload/",
                  "/upload/c_scale,w_400,f_auto,q_auto/"
                )
              : "https://via.placeholder.com/300",
            tag: item["Tag"] || "",
          };

          const isPromo =
            menuItem.tag && menuItem.tag.toLowerCase().includes("promo");

          if (isPromo) {
            promoCategory.items.push(menuItem);
            allItemsCategory.items.unshift(menuItem);
          } else {
            allItemsCategory.items.push(menuItem);
          }

          let catGroup = processedCategories.find(
            (c) => c.category === item["Kategori"]
          );
          if (!catGroup) {
            catGroup = { category: item["Kategori"] || "LAINNYA", items: [] };
            processedCategories.push(catGroup);
          }
          catGroup.items.push(menuItem);
        });

        const finalMenuData = [allItemsCategory];
        if (promoCategory.items.length > 0) finalMenuData.push(promoCategory);

        finalMenuData.push(...processedCategories);

        setMenuData(finalMenuData);
        setActiveCategory("SEMUA");
      } catch (error) {
        console.error("Error fetching Google Sheets:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenu();
  }, [onAddonsLoaded]);

  const filteredItems = useMemo(() => {
    const currentCategoryItems =
      menuData.find((c) => c.category === activeCategory)?.items || [];
    if (!searchTerm) return currentCategoryItems;

    return currentCategoryItems.filter(
      (item) =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.description &&
          item.description.toLowerCase().includes(searchTerm.toLowerCase()))
    );
  }, [menuData, activeCategory, searchTerm]);

  const formatIDR = (price: number) => {
    return new Intl.NumberFormat("id-ID", {
      style: "currency",
      currency: "IDR",
      minimumFractionDigits: 0,
    }).format(price);
  };

  return (
    <section className="pt-28 pb-24 min-h-screen bg-slate-50">
      <Helmet>
        <title>Daftar Menu & Harga Hisana Padang - Promo Spesial</title>
        <meta
          name="description"
          content="Cek harga paket ayam krispi, burger, dan snack Hisana Padang terlengkap."
        />
      </Helmet>

      <div className="container mx-auto px-4 sm:px-6">
        <SectionHeading
          title="Daftar Menu & Harga Hisana Fried Chicken Padang"
          subtitle="Cek harga menu paket Hisana terbaru 2026. Tersedia di outlet Gajah Mada, Siteba, Kalawi, dan seluruh Padang."
        />

        <div className="max-w-4xl mx-auto mb-12 space-y-6">
          <div className="relative group px-4">
            <Search
              className="absolute left-8 top-1/2 -translate-y-1/2 text-slate-400"
              size={20}
            />
            <input
              type="text"
              placeholder="Cari menu atau paket Hisana favoritmu..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-14 pr-12 py-4 bg-white border border-slate-200 rounded-2xl shadow-sm focus:ring-2 focus:ring-[#ee1d23]/20 outline-none transition-all font-medium"
            />
          </div>

          <div className="flex flex-nowrap sm:flex-wrap gap-2 overflow-x-auto pb-2 no-scrollbar px-4">
            {menuData.map((cat) => (
              <button
                key={cat.category}
                onClick={() => {
                  setActiveCategory(cat.category);
                  setSearchTerm("");
                }}
                aria-label="Cari menu"
                className={`whitespace-nowrap px-6 py-2.5 rounded-full font-bold text-[10px] uppercase tracking-widest transition-all ${
                  activeCategory === cat.category
                    ? "bg-[#ee1d23] text-white shadow-md scale-105"
                    : "bg-white text-slate-500 border border-slate-200 hover:border-rose-200"
                }`}
              >
                {cat.category}
              </button>
            ))}
          </div>
        </div>

        {/* LOGIC LOADING & KONTEN MENU */}
        {isLoading ? (
          <div className="flex flex-col items-center justify-center py-32 space-y-4">
            <Loader2 className="w-12 h-12 text-[#ee1d23] animate-spin" />
            <p className="text-slate-500 font-medium text-sm sm:text-base tracking-wide animate-pulse">
              Memuat menu favoritmu...
            </p>
          </div>
        ) : filteredItems.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-6 px-4">
            {filteredItems.map((item) => (
              <div
                key={item.id}
                className="group rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden flex flex-col h-full border bg-white border-slate-100 shadow-sm hover:shadow-xl transition-all duration-300 relative"
              >
                <div
                  className="relative h-40 sm:h-48 overflow-hidden cursor-pointer"
                  onClick={() => onOpenDetail?.(item)}
                >
                  <img
                    src={item.image}
                    alt={`${item.name} Hisana Fried Chicken Padang - Harga ${formatIDR(item.priceNum)}`}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    loading="lazy"
                  />
                  {item.tag && (
                    <div className="absolute top-3 left-3 bg-[#ee1d23] text-white text-[8px] font-black uppercase px-3 py-1 rounded-full shadow-lg z-10">
                      {item.tag}
                    </div>
                  )}
                  <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <div className="bg-white/90 p-2 rounded-full text-[#ee1d23] shadow-lg">
                      <Info size={16} />
                    </div>
                  </div>
                </div>

                <div className="p-4 flex flex-col flex-grow">
                  <h4 className="text-xs sm:text-sm font-bold text-slate-900 mb-1 line-clamp-1 uppercase">
                    {item.name}
                  </h4>
                  <span className="text-[#ee1d23] font-black text-xs sm:text-sm mb-3">
                    {formatIDR(item.priceNum)}
                  </span>
                  <p className="text-[10px] text-slate-500 mb-4 line-clamp-2 leading-relaxed h-8">
                    {item.description}
                  </p>

                  <div className="flex gap-2 mt-auto">
                    <button
                      onClick={() => onAddToCart(item)}
                      aria-label={`Tambah ${item.name} ke keranjang`}
                      className="flex-grow bg-[#ee1d23] text-white py-2.5 rounded-xl font-bold text-[8px] flex items-center justify-center gap-2 active:scale-95 transition-all shadow-md hover:bg-rose-600"
                    >
                      <Plus size={14} /> TAMBAH
                    </button>
                    <button
                      onClick={() => onCheckout(item)}
                      aria-label={`Beli ${item.name} sekarang`}
                      className="p-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                    >
                      <ChevronRight size={16} className="text-slate-400" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 bg-white rounded-3xl border-2 border-dashed border-slate-100 mx-4">
            <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">
              Menu tidak ditemukan
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default MenuView;